import { z } from "zod";

import type { GetEntitiesQuery, GetEntityDetailQuery, GetEntityRelationLifecycleQuery } from "./gqls";

/** List view modes. */
const zEntityView = z.union([z.literal("table"), z.literal("timeline"), z.literal("map")]);
export type EntityView = z.infer<typeof zEntityView>;

/** Sortable columns of the entity table. Validated against URL search params. */
const zEntitySortOrder = z.union([z.literal("name"), z.literal("nameJa"), z.literal("date"), z.literal("updatedAt")]);
export type EntitySortOrder = z.infer<typeof zEntitySortOrder>;

const zSortDirection = z.union([z.literal("asc"), z.literal("desc")]);
export type SortDirection = z.infer<typeof zSortDirection>;

/** Detail-page tabs. */
const zEntityDetailTab = z.union([z.literal("data"), z.literal("changeRequests"), z.literal("lineage")]);
export type EntityDetailTab = z.infer<typeof zEntityDetailTab>;

/** Map viewport persisted to the URL (lat/lng/zoom). */
export interface MapViewport {
  latitude: number;
  longitude: number;
  zoom: number;
}

/** UI-side filter shape resolved from URL params; drives both the query and the sidebar. */
export interface EntityListFilters {
  idOrName?: string;
  categoryIds: string[];
  referenceIds: string[];
  excludeDeactivated: boolean;
  gdtfFrom?: string;
  gdtfTo?: string;
}

export type EntityRaw = GetEntitiesQuery["records"][number];

export type EntityDetailRaw = GetEntityDetailQuery["records"][number];
export type RelatedCompoundEntityRaw = GetEntityDetailQuery["relatedCompoundEntities"][number];

/** A category chip shown on a row / detail. */
export interface EntityCategoryChip {
  id: string;
  label: string;
}

/** UI-ready table row passed from the container to the table. */
export interface EntityRow {
  id: string;
  nameJa: string;
  dateRange: string;
  updatedAt: string;
  categories: EntityCategoryChip[];
  /** First active geo-item id, when present (drives the "Area" action). */
  geoItemId: string | null;
  isDeactivated: boolean;
}

/** A related entity reached through a relation record. */
export interface RelatedEntityLink {
  id: string;
  displayName: string;
  categories: EntityCategoryChip[];
}

/** The latest compound entity containing the record shown on the detail page. */
export interface RelatedCompoundEntity {
  id: string;
  key: string;
  displayName: string;
  version: string;
}

/** One source/target relation edge shown in the detail relation list. */
export interface EntityRelationRow {
  relationId: string;
  categories: EntityCategoryChip[];
  /** The relation record's own date range, not the related entity's. */
  dateRange: string;
  related: RelatedEntityLink[];
}

/**
 * A relation attached to the entity, seen from the activate/deactivate dialog.
 *
 * `blockedByEndpoint` means the relation is deactivated and at least one of its other endpoints
 * is too, so activating it alongside this entity would leave a live relation pointing at a
 * record that is gone. Those are shown but cannot be selected.
 */
export interface EntityRelationLifecycleRow {
  relationId: string;
  displayName: string;
  isDeactivated: boolean;
  blockedByEndpoint: boolean;
}

/**
 * How many relations one activation may bring back.
 *
 * Mirrors the server's cap (`CascadeRelationContextFunction.MAX_CASCADE_RELATION_IDS`), which
 * exists because each id is a versioned write inside the approval's transaction. Kept in step
 * by hand: exceeding it means the request is refused outright, so the dialog stops before it
 * builds one rather than letting a default selection fail on submit.
 */
export const MAX_CASCADE_RELATION_IDS = 500;

/** The dialog's view of what a lifecycle change would do to the entity's relations. */
export interface EntityRelationLifecycle {
  /** Live relations, which a deactivation takes down with the entity. */
  toDeactivate: EntityRelationLifecycleRow[];
  /** Deactivated relations, which an activation may bring back if nothing else blocks them. */
  activatable: EntityRelationLifecycleRow[];
}

export type EntityRelationLifecycleRaw = GetEntityRelationLifecycleQuery["records"][number];

/** A single geo-item attached to the entity. */
export interface EntityGeoItemRow {
  id: string;
  label: string;
  heading: string | null;
  date: string;
  endDate: string;
  countryIds: string[];
  deactivatedAt: string | null;
  isDeactivated: boolean;
}

/** A change-request item row shown on the detail Change Requests tab. */
export interface EntityChangeRequestRow {
  id: string;
  createdAt: string;
  operationType: string;
  properties: unknown;
  changeRequestId: string;
  description: string;
  userName: string;
  /** Raw change-request context JSON (rendered as a structured table). */
  context: unknown;
}

/** An evidence row shown in the detail EvidencesBox. */
export interface EntityEvidenceRow {
  id: string;
  date: string;
  name: string;
  reference: { id: string; shortCode: string; title: string } | null;
  userName: string;
  updatedAt: string | null;
}

export type LineageNodeType = "entity" | "changeRequest" | "evidence" | "reference";

/** A node in the detail Lineage graph. */
export interface LineageNode {
  id: string;
  type: LineageNodeType;
  label: string;
  subLabel: string;
  /** Open / approved change requests are shown muted (not yet applied to the record). */
  muted?: boolean;
}

/** Kind of lineage edge — determines click-through navigation (mirrors V1). */
export type LineageEdgeType =
  | "changeRequestToRecord"
  | "evidenceToChangeRequest"
  | "evidenceToEvidence"
  | "referenceToEvidence";

/** An edge in the detail Lineage graph. */
export interface LineageEdge {
  id: string;
  source: string;
  target: string;
  type: LineageEdgeType;
  /** Data-import-project this link came from (drives edge click-through to the DIP job peek). */
  projectID?: string;
  /** Batch-change-requests job id (for `evidenceToChangeRequest` edges). */
  changeRequestJobID?: string;
  /** Batch-evidences job id (for `referenceToEvidence` edges). */
  evidenceJobID?: string;
}

export interface EntityLineage {
  nodes: LineageNode[];
  edges: LineageEdge[];
}

/** A single property row inside a category's schema table (position · name · value). */
interface EntityPropertyRow {
  position: number;
  name: string;
  value: unknown;
  propertyTypeId: string;
  selectOptions: unknown;
}

/** Properties grouped under a category, ordered by schema position (V1 parity). */
export interface EntityPropertyGroup {
  categoryId: string;
  categoryLabel: string;
  rows: EntityPropertyRow[];
}

/** Fields shown on the read-only detail Data tab. */
export interface EntityDetailUI {
  id: string;
  displayName: string;
  dateRange: string;
  createdAt: string | null;
  updatedAt: string | null;
  deactivatedAt: string | null;
  isDeactivated: boolean;
  categories: EntityCategoryChip[];
  /** Schema-ordered property tables grouped per category (V1 RecordDetailData parity). */
  propertyGroups: EntityPropertyGroup[];
  /** Properties not covered by any category's schema, rendered as a JsonTable. */
  otherProperties: Record<string, unknown>;
  /** The full record-metadata object, dumped as a JsonTable on the Metadata card (V1 parity). */
  metadata: unknown;
  geoItems: EntityGeoItemRow[];
  sourceRelations: EntityRelationRow[];
  targetRelations: EntityRelationRow[];
  compoundEntity: RelatedCompoundEntity | null;
}
